import { GetStaticProps, GetStaticPaths, NextPage } from 'next';
import { SimplePage, PageSlug } from 'interfaces/content/articles/Post';
import { HeaderNav } from 'interfaces/navigation/Header';
import { StaticPage } from 'pages/Static/StaticPage';
import getPageByURI from 'api/Page/getPageByURI';
import getAllPages from 'api/Page/getAllPages';
import { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import { setMockPianoConfig } from 'services/Piano';
import { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import getFooter from 'api/Footer/getFooter';
import { HeadData, setHeadData } from 'utils/metaDataUtils/headData';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import NotFoundError from 'interfaces/errors/NotFoundError';
import getMenu from 'api/Header/getMenu';
import { PreviewData } from 'interfaces/PreviewData';
import mockPageData from 'lib/mockData/page-data';
import { ArticleProvider } from 'providers/ArticleProvider';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';

interface PageProps {
  page: SimplePage;
}

const StaticNextPage: NextPage<PageProps> = ({ page }: PageProps) => (
  <ArticleProvider page={page}>
    <StaticPage page={page} />
  </ArticleProvider>
);

export default StaticNextPage;

interface Props {
  params: {
    param: string;
  };
}

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { param } = context.params as { param: string };
    const { preview, previewData } = context as { preview: boolean; previewData: PreviewData };
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );

    const footer = await safeAsync(() => getFooter(), fallbackData.footer);

    if (param === 'subscribe') {
      menu.topicNavigation = [];
    }

    const page = await getPageByURI(param, previewData);

    if (page.destination !== 'main' || page.contentType === 'hidden') throw new NotFoundError('Page not found');

    const pageData = preview ? mockPageData : page;

    const headData: HeadData = safe(() => setHeadData(pageData), fallbackData.headData);
    const arcSpanPageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(pageData),
      fallbackData.arcSpanPageConfig,
    );
    const pianoPageConfig: PianoPageConfig = safe(() => setMockPianoConfig(), fallbackData.pianoPageConfig);

    return {
      props: {
        arcSpanPageConfig,
        footer,
        headData,
        headerNav: menu,
        page,
        pianoPageConfig,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<{ param: string }> = async () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: {
            param: 'privacy-policy',
          },
        },
      ],
    };
  }

  const { pages }: { pages: PageSlug[] } = await getAllPages();

  const paths: Props[] = pages.map((page) => ({
    params: {
      param: page.slug,
    },
  }));

  return {
    fallback: 'blocking',
    paths,
  };
};
